import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Check, Lock, ArrowLeft, Loader2, Sparkles, ArrowRight, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { TimeLeft } from "../components/TimeLeft";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "test";

export default function CheckoutPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { currency } = useCurrency();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [stripeLoading, setStripeLoading] = useState(false);
  const [paypalState, setPaypalState] = useState({ loading: false });
  const [coupon, setCoupon] = useState(null); // {code, discount_pct, price, original_price}
  const [couponError, setCouponError] = useState("");

  useEffect(() => {
    api
      .get(`/products/${productId}?currency=${currency}`)
      .then((r) => setProduct(r.data))
      .catch(() => navigate("/"));
  }, [productId, currency, navigate]);

  // Auto-apply coupon from URL ?coupon=...
  useEffect(() => {
    const code = searchParams.get("coupon");
    if (!code) {
      setCoupon(null);
      setCouponError("");
      return;
    }
    api
      .get(`/coupons/validate`, {
        params: { code, product_id: productId, currency, customer_email: email || undefined },
      })
      .then((r) => {
        if (r.data.valid) {
          setCoupon(r.data);
          setCouponError("");
        } else {
          setCoupon(null);
          setCouponError(r.data.reason || "Coupon could not be applied");
        }
      })
      .catch(() => {
        setCoupon(null);
        setCouponError("Could not verify coupon.");
      });
  }, [searchParams, productId, currency, email]);

  // Load bundle for upsell (skip if already on bundle)
  useEffect(() => {
    if (productId === "bundle-all-5") {
      setBundle(null);
      return;
    }
    api
      .get(`/products/bundle-all-5?currency=${currency}`)
      .then((r) => setBundle(r.data))
      .catch(() => setBundle(null));
  }, [currency, productId]);

  // Abandoned-cart tracking — debounced on email change
  useEffect(() => {
    if (!/\S+@\S+\.\S+/.test(email)) return;
    const handle = setTimeout(() => {
      api
        .post("/cart/track", {
          customer_email: email,
          product_id: productId,
          currency,
          customer_name: name || undefined,
          origin_url: window.location.origin,
        })
        .catch(() => {});
    }, 1200);
    return () => clearTimeout(handle);
  }, [email, name, productId, currency]);

  const validEmail = email && /\S+@\S+\.\S+/.test(email);

  const handleStripe = async () => {
    if (!validEmail) {
      toast.error("Please enter a valid email to receive your download.");
      return;
    }
    setStripeLoading(true);
    try {
      const { data } = await api.post("/checkout/stripe", {
        product_id: productId,
        currency,
        origin_url: window.location.origin,
        customer_email: email,
        customer_name: name,
        coupon_code: coupon?.code,
      });
      window.location.href = data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start Stripe checkout. Please try again.";
      toast.error(msg);
      setStripeLoading(false);
    }
  };

  const handlePaypalCreate = async () => {
    if (!validEmail) {
      toast.error("Please enter a valid email first.");
      throw new Error("missing-email");
    }
    const { data } = await api.post("/checkout/paypal/create", {
      product_id: productId,
      currency,
      customer_email: email,
      customer_name: name,
      coupon_code: coupon?.code,
    });
    if (data.mock) {
      // Mock flow: capture immediately
      const capture = await api.post("/checkout/paypal/capture", {
        paypal_order_id: data.order_id,
      });
      navigate(
        `/thank-you?provider=paypal&token=${capture.data.download_token}&product=${capture.data.product_id}`
      );
      throw new Error("mock-handled");
    }
    return data.order_id;
  };

  const handlePaypalApprove = async (data) => {
    const res = await api.post("/checkout/paypal/capture", {
      paypal_order_id: data.orderID,
    });
    if (res.data.download_token) {
      navigate(
        `/thank-you?provider=paypal&token=${res.data.download_token}&product=${res.data.product_id}`
      );
    } else {
      toast.error("Payment was not completed. Please try again.");
    }
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="bg-[#F8FAFC] min-h-screen">
      <Header />

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0A192F] mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to all plans
        </Link>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Order Summary */}
          <aside className="lg:col-span-2">
            <div className="bg-white border border-slate-200 p-8 sticky top-28">
              <span className="font-accent text-[#C5A059] text-xs">YOUR ORDER</span>
              <h2 className="font-display text-2xl text-[#0A192F] mt-3 mb-1">
                {product.name}
              </h2>
              <p className="text-slate-600 text-sm mb-6">{product.tagline}</p>

              <ul className="space-y-2 mb-6 pb-6 border-b border-slate-200">
                {product.highlights.slice(0, 5).map((h) => (
                  <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-[#C5A059] flex-shrink-0 mt-0.5" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-baseline justify-between mb-2">
                <span className="text-slate-500 text-sm line-through">
                  {product.symbol}
                  {product.original_price.toFixed(2)}
                </span>
                <span className={`font-accent text-[10px] ${product.on_sale ? "text-[#D4AF37]" : "text-[#C5A059]"}`}>
                  {product.on_sale ? (product.sale_label || "SALE") : "FOUNDER DISCOUNT"}
                </span>
              </div>
              {product.on_sale && product.sale_expires_at && (
                <div className="mb-3">
                  <TimeLeft
                    expiresAt={product.sale_expires_at}
                    label={product.sale_label ? `${product.sale_label} ENDS IN` : "ELITE SALE ENDS IN"}
                  />
                </div>
              )}
              {coupon && (
                <div
                  data-testid="coupon-applied-banner"
                  className="mt-3 mb-1 flex items-center justify-between gap-2 bg-[#C5A059]/10 border border-[#C5A059]/40 px-3 py-2"
                >
                  <div className="flex items-center gap-2 text-[12px] text-[#0A192F]">
                    <Tag className="w-3.5 h-3.5 text-[#C5A059]" />
                    <span className="font-accent text-[10px]">{coupon.code}</span>
                    <span className="text-slate-500">applied · −{coupon.discount_pct}%</span>
                  </div>
                  <Link
                    to={`/checkout/${productId}`}
                    data-testid="coupon-remove"
                    className="text-slate-400 hover:text-slate-700"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Link>
                </div>
              )}
              {couponError && (
                <div
                  data-testid="coupon-error"
                  className="mt-3 mb-1 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2"
                >
                  {couponError}
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <span className="text-slate-700">Total</span>
                <span
                  data-testid="checkout-total"
                  className={`font-display text-3xl ${product.on_sale && !coupon ? "text-[#D4AF37]" : "text-[#0A192F]"}`}
                >
                  {product.symbol}
                  {coupon ? coupon.price.toFixed(2) : product.price.toFixed(2)}
                </span>
              </div>

              {bundle && (
                <Link
                  to={`/checkout/${bundle.id}`}
                  data-testid="checkout-upsell-bundle"
                  className="group mt-6 block bg-[#0A192F] hover:bg-[#112240] text-white p-4 transition"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-3.5 h-3.5 text-[#C5A059]" />
                    <span className="font-accent text-[10px] text-[#C5A059]">UPGRADE OFFER</span>
                  </div>
                  <div className="text-sm font-semibold leading-snug mb-1">
                    Add the other 4 plans for just{" "}
                    {product.symbol}
                    {(bundle.price - product.price).toFixed(2)} more
                  </div>
                  <div className="text-[11px] text-white/60 flex items-center gap-1">
                    Swap to Complete Founder's Bundle — {bundle.symbol}
                    {bundle.price.toFixed(2)} total · save {Math.round(((bundle.original_price - bundle.price) / bundle.original_price) * 100)}%
                    <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition" />
                  </div>
                </Link>
              )}

              <div className="mt-6 pt-6 border-t border-slate-200 text-xs text-slate-500 flex items-center gap-2">
                <Lock className="w-3.5 h-3.5" />
                Secure checkout &middot; 256-bit SSL
              </div>
            </div>
          </aside>

          {/* Payment Form */}
          <div className="lg:col-span-3 bg-white border border-slate-200 p-8">
            <h1 className="font-display text-3xl text-[#0A192F] mb-2">Checkout</h1>
            <p className="text-slate-600 text-sm mb-8">
              Complete the form and choose your preferred payment method.
            </p>

            <div className="space-y-4 mb-8">
              <div>
                <label className="text-xs font-accent text-slate-500 mb-1.5 block">
                  EMAIL (FOR DELIVERY)
                </label>
                <input
                  data-testid="checkout-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]"
                />
              </div>
              <div>
                <label className="text-xs font-accent text-slate-500 mb-1.5 block">
                  NAME (OPTIONAL)
                </label>
                <input
                  data-testid="checkout-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Founder"
                  className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059]"
                />
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleStripe}
                disabled={stripeLoading || !validEmail}
                data-testid="pay-with-stripe-btn"
                className="w-full bg-[#0A192F] hover:bg-[#112240] disabled:opacity-50 text-white font-bold py-4 text-sm transition flex items-center justify-center gap-2"
              >
                {stripeLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>PAY WITH CARD &middot; STRIPE</>
                )}
              </button>

              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-3 text-slate-400 font-accent">OR</span>
                </div>
              </div>

              <div data-testid="paypal-buttons-wrapper" className={!validEmail ? "opacity-50 pointer-events-none" : ""}>
                <PayPalScriptProvider
                  options={{ "client-id": PAYPAL_CLIENT_ID, currency }}
                >
                  <PayPalButtons
                    style={{ layout: "horizontal", color: "gold", shape: "rect", label: "paypal", height: 48 }}
                    forceReRender={[currency, email, product.price]}
                    createOrder={async () => {
                      try {
                        return await handlePaypalCreate();
                      } catch (e) {
                        // swallow — mock or missing email handled
                        return "";
                      }
                    }}
                    onApprove={handlePaypalApprove}
                    onError={() => toast.error("PayPal could not complete the payment.")}
                  />
                </PayPalScriptProvider>
              </div>

              {!validEmail && (
                <p data-testid="email-required-warning" className="text-xs text-slate-500 text-center">
                  Enter your email to enable payment.
                </p>
              )}

              <p className="text-xs text-slate-500 text-center pt-4">
                By completing the purchase you agree to our terms. All sales are final due to instant digital delivery.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
